import { PlayCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { ISLANDS } from "../../lib/constants";

export const HealthTab = ({ health, reload }) => (
  <div className="space-y-4" data-testid="admin-health-view">
    <div className="card-soft p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm uppercase tracking-wider text-[#1A1A1A]">
          Scraper Health
        </h3>
        <span className="text-xs text-[#6B7280]">
          Green = under 90 min ago · Yellow = 90–180 min · Red = over 180 min
        </span>
      </div>
      <div className="space-y-2">
        {ISLANDS.filter((i) => i.slug !== "all").map((i) => (
          <div key={i.slug} data-testid={`admin-health-island-${i.slug}`}>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mt-3 mb-1 flex items-center gap-2">
              <span>{i.flag}</span>
              <span>{i.name}</span>
            </div>
            {health
              .filter((h) => h.island === i.slug)
              .map((h) => {
                const lastTs = h.last_success_at
                  ? new Date(h.last_success_at).getTime()
                  : 0;
                const mins = lastTs ? (Date.now() - lastTs) / 60000 : null;
                const dotColor =
                  mins === null
                    ? "#9CA3AF"
                    : mins < 90
                    ? "#3BB273"
                    : mins < 180
                    ? "#F0A500"
                    : "#E63946";
                const ageLabel =
                  mins === null
                    ? "never"
                    : mins < 60
                    ? `${Math.round(mins)}m ago`
                    : `${Math.round(mins / 60)}h ago`;
                const nameId = h.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                return (
                  <div
                    key={h.name}
                    className="flex items-center gap-3 px-3 py-2.5 rounded border border-[#EEEEEE] bg-white"
                    data-testid={`admin-health-row-${nameId}`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: dotColor }}
                      aria-label={ageLabel}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#1A1A1A] font-medium truncate flex items-center gap-2">
                        <span className="truncate">{h.name}</span>
                        {h.timed_out_this_cycle && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFF3D6] text-[#8A5A00] border border-[#F0A500]/30 text-[10px] uppercase tracking-wide font-semibold flex-shrink-0"
                            data-testid={`admin-health-timeout-badge-${nameId}`}
                            title="This source hit the 45s per-source ceiling last cycle. Likely offline or blocking our IP — the whole scrape cycle would freeze on it without this guard."
                          >
                            <AlertTriangle size={10} />
                            Auto-timeout
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#6B7280] truncate">
                        Last success: {ageLabel} · 24h fetched: {h.recent_24h}
                        {h.last_seen != null && ` · last run: ${h.last_seen} items`}
                        {h.last_skipped > 0 && ` · ${h.last_skipped} skipped`}
                      </div>
                      {h.last_error && (
                        <div
                          className="text-[11px] text-[#E63946] truncate mt-0.5"
                          data-testid={`admin-health-error-${nameId}`}
                        >
                          Error: {h.last_error}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await api.post(`/admin/sources/${encodeURIComponent(h.name)}/scrape`);
                          toast.success(`Scraping ${h.name}…`);
                          setTimeout(reload, 6000);
                        } catch {
                          toast.error("Failed to start scrape");
                        }
                      }}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0AADA0]/10 text-[#0AADA0] hover:bg-[#0AADA0]/20 text-xs flex-shrink-0"
                      data-testid={`admin-health-scrape-${nameId}`}
                    >
                      <PlayCircle size={12} /> Scrape now
                    </button>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  </div>
);
